import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

// =============================================================================
// Types
// =============================================================================

export interface DesignAsset {
  id: string
  project_id: string
  user_id: string
  type: 'image' | 'text' | 'color' | 'link'
  title: string | null
  description: string | null
  file_url: string | null
  file_name: string | null
  file_size: number | null
  file_type: string | null
  content: string | null
  category: 'logo' | 'palette' | 'inspiration' | 'mockup' | 'photo' | 'typography' | 'other'
  order_index: number
  created_at: string
  updated_at: string
}

export type DesignCategory = DesignAsset['category']

// =============================================================================
// Query Key
// =============================================================================

const DESIGN_ASSETS_KEY = 'admin-design-assets'

// =============================================================================
// Helpers
// =============================================================================

function extractStoragePath(publicUrl: string, bucketName: string): string | null {
  try {
    const signedMarker = `/storage/v1/object/sign/${bucketName}/`
    let markerIndex = publicUrl.indexOf(signedMarker)
    if (markerIndex !== -1) {
      const pathWithParams = publicUrl.substring(markerIndex + signedMarker.length)
      return pathWithParams.split('?')[0] || null
    }

    const publicMarker = `/storage/v1/object/public/${bucketName}/`
    markerIndex = publicUrl.indexOf(publicMarker)
    if (markerIndex !== -1) {
      return publicUrl.substring(markerIndex + publicMarker.length) || null
    }

    return null
  } catch {
    return null
  }
}

// =============================================================================
// Hooks
// =============================================================================

/**
 * Hook pour récupérer tous les design assets d'un projet (lecture seule, côté admin)
 */
export function useDesignAssets(projectId: string | undefined) {
  return useQuery({
    queryKey: [DESIGN_ASSETS_KEY, projectId],
    queryFn: async (): Promise<DesignAsset[]> => {
      const { data, error } = await supabase
        .from('project_design_assets')
        .select('*')
        .eq('project_id', projectId!)
        .order('category', { ascending: true })
        .order('order_index', { ascending: true })

      if (error) throw error

      const assets = (data as DesignAsset[]) || []

      // Générer des URLs signées pour les images
      const imageAssets = assets.filter((a) => a.type === 'image' && a.file_url)

      const pathEntries: { assetId: string; storagePath: string }[] = []
      for (const asset of imageAssets) {
        const url = asset.file_url!
        if (url.startsWith('http')) {
          const extracted = extractStoragePath(url, 'project-designs')
          if (extracted) {
            pathEntries.push({ assetId: asset.id, storagePath: extracted })
          }
        } else {
          pathEntries.push({ assetId: asset.id, storagePath: url })
        }
      }

      if (pathEntries.length > 0) {
        const paths = pathEntries.map((e) => e.storagePath)
        const { data: signedUrls, error: signError } = await supabase.storage
          .from('project-designs')
          .createSignedUrls(paths, 60 * 60)

        if (!signError && signedUrls) {
          const urlMap = new Map<string, string>()
          for (const item of signedUrls) {
            if (item.signedUrl && item.path) {
              urlMap.set(item.path, item.signedUrl)
            }
          }

          const assetUrlMap = new Map<string, string>()
          for (const entry of pathEntries) {
            const signed = urlMap.get(entry.storagePath)
            if (signed) {
              assetUrlMap.set(entry.assetId, signed)
            }
          }

          return assets.map((asset) => {
            if (assetUrlMap.has(asset.id)) {
              return { ...asset, file_url: assetUrlMap.get(asset.id)! }
            }
            return asset
          })
        }
      }

      return assets
    },
    enabled: !!projectId,
    staleTime: 2 * 60 * 1000,
  })
}

/**
 * Hook pour récupérer les design assets groupés par catégorie
 */
export function useDesignAssetsByCategory(projectId: string | undefined) {
  const query = useDesignAssets(projectId)

  const groupedData = useMemo(() => {
    if (!query.data) return {} as Record<DesignCategory, DesignAsset[]>

    return query.data.reduce<Record<string, DesignAsset[]>>((acc, asset) => {
      const cat = asset.category
      if (!acc[cat]) acc[cat] = []
      acc[cat].push(asset)
      return acc
    }, {})
  }, [query.data])

  return { ...query, data: groupedData }
}

/**
 * Hook pour compter les assets par catégorie
 */
export function useDesignAssetCounts(projectId: string | undefined) {
  const query = useDesignAssets(projectId)

  const counts = useMemo(() => {
    if (!query.data) return {} as Record<string, number>

    return query.data.reduce<Record<string, number>>((acc, asset) => {
      acc[asset.category] = (acc[asset.category] || 0) + 1
      return acc
    }, {})
  }, [query.data])

  return { ...query, data: counts }
}
